// The Curve tab's tiles (spec clip-speed-curves-tab, layout A): None, Custom and the six presets,
// two rows of four, each its shape and the length it gives the clip, as the reducer lays it.
import type { SpeedCurveName } from "../../api/generated/model";
import { speedY } from "./curve-edit";
import { CURVE_PRESETS, CUSTOM_START, curveOver, curved, type CurvePoint } from "./speed-curve";
import { lengthWith, spanOf, type AudioClip, type VideoClip } from "./timeline";
import classes from "./curves.module.css";

/** The tiles in their order: None (one speed), Custom, then the six presets. */
export const CURVE_TILES: readonly { name: SpeedCurveName | null; label: string }[] = [
  { name: null, label: "None" },
  { name: "custom", label: "Custom" },
  { name: "montage", label: "Montage" },
  { name: "hero", label: "Hero" },
  { name: "bullet", label: "Bullet" },
  { name: "jump_cut", label: "Jump cut" },
  { name: "flash_in", label: "Flash in" },
  { name: "flash_out", label: "Flash out" },
];

/** A curve's name on the block: its tile's, and "Curve" for Custom (layout A's badge). */
export function curveLabel(name: SpeedCurveName): string {
  if (name === "custom") return "Curve";
  return CURVE_TILES.find((tile) => tile.name === name)?.label ?? name;
}

/**
 * The shape a tile lays over the clip: its preset's, Custom's own points while the clip plays
 * Custom (else Custom's start, five points at 1×), and none for None.
 */
export function tileShape(clip: VideoClip | AudioClip, name: SpeedCurveName | null): readonly CurvePoint[] | null {
  if (name === null) return null;
  if (name !== "custom") return CURVE_PRESETS[name];
  return clip.speedCurve === "custom" && curved(clip) ? curveOver(clip.speedPoints, clip.inMs, spanOf(clip)) : CUSTOM_START;
}

const ONE_SPEED: readonly CurvePoint[] = [
  { at: 0, speed: 1 },
  { at: 1, speed: 1 },
];
const seconds = (ms: number) => `${(ms / 1000).toFixed(1)} s`;

/** A curve's shape, speed up by halvings as the editor draws it, so it is straight between points. */
export function CurveShape({ points }: { points: readonly CurvePoint[] }) {
  const path = points.map((point, at) => `${at === 0 ? "M" : "L"}${(point.at * 100).toFixed(2)} ${speedY(point.speed, 22).toFixed(2)}`).join(" ");
  return (
    <svg className={classes.shape} viewBox="0 0 100 22" preserveAspectRatio="none" aria-hidden="true">
      <path d={path} />
    </svg>
  );
}

export function CurveTiles(props: {
  clip: VideoClip | AudioClip;
  disabled: boolean;
  onPick: (name: SpeedCurveName | null, shape: readonly CurvePoint[] | null) => void;
}) {
  const { clip, disabled, onPick } = props;
  // None is one speed at 1×: a clip at another speed has no tile pressed.
  const pressed = curved(clip) ? clip.speedCurve : clip.speed === 1 ? null : undefined;
  return (
    <ul className={classes.tiles} aria-label="Speed curves">
      {CURVE_TILES.map(({ name, label }) => {
        const shape = tileShape(clip, name);
        const lasts = seconds(lengthWith(clip, shape));
        return (
          <li key={label}>
            <button
              type="button"
              className={classes.tile}
              aria-label={`${label}: lasts ${lasts}`}
              aria-pressed={pressed === name}
              disabled={disabled}
              onClick={() => onPick(name, shape)}
            >
              <CurveShape points={shape ?? ONE_SPEED} />
              <span className={classes.tileName}>{label}</span>
              <span className={classes.tileLength}>{lasts}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
